import { Box } from "@chakra-ui/react";
import {
  Badge,
  call,
  ColorIcon,
  colors,
  customEvent,
  getCdnImageSrc,
  HStack,
  Image,
  LargeButton,
  LARGEL_BUTTON_TYPE,
  NextLink,
  SlidingMenu,
  VStack,
} from "goi_common";
import React, { useState } from "react";
import { css } from "@emotion/react";
import SeaBurialBreadcrumb from "./components/SeaBurialBreadcrumb";
import FacilitiesCallFixedButtons from "@/containers/FacilitiesContainer/components/FacilitiesCallFixedButtons";

import { useOffDirectCall } from "@/hooks/useOffDirectCall";

const SLIDING_ITEMS = ["시설 정보", "시설 사용료"];

export default function FacilitiesDetailSeaBurialContainer({
  seaBurialData,
}: {
  seaBurialData: any;
}) {
  const [current, setCurrent] = useState<number>(0);
  const 오인지_최소화_기능 = useOffDirectCall();

  return (
    <VStack width="100%" alignItems="center">
      <VStack width="100%" maxWidth="480px">
        <VStack width="100%" height="240px" margin="0 0 30px">
          <Image
            src={getCdnImageSrc(`platform/sea-burial/${seaBurialData.id}.png`)}
            alt={seaBurialData.companyname}
          />
        </VStack>
        <VStack padding="20px" gap="4px">
          <SeaBurialBreadcrumb
            sidoName={seaBurialData.district}
            companyname={seaBurialData.companyname}
          />

          <h1 className="subtitle2_b gray900">{seaBurialData.companyname}</h1>

          <HStack width="100%" alignItems="flex-start" gap="5px">
            <ColorIcon name="rating_star" color={colors.orange650} size={18} />

            <Box className="body3_m gray400">- / 5.0</Box>
            <Badge className="caption_12_b" color={colors.gray400} padding="2px 6px">
              바다
            </Badge>
          </HStack>
        </VStack>

        <SlidingMenu
          current={current}
          setCurrent={(v) => {
            customEvent({
              newGtm: { path: "sea_burial_detail", trigger_and_target: "tab-facility_tab" },
            });
            setCurrent(v);
          }}
          items={SLIDING_ITEMS}
        />

        {current === 0 && <SeaBurialCTA seaBurial={seaBurialData} />}
        {current === 1 && <SeaBurialFee seaBurial={seaBurialData} />}
      </VStack>
      {!오인지_최소화_기능 && <FacilitiesCallFixedButtons />}
    </VStack>
  );
}

function SeaBurialCTA({ seaBurial }: { seaBurial: any }) {
  return (
    <VStack>
      <VStack gap="24px" padding="32px 24px" background={colors.gray50}>
        <Box>
          장례식을 치르는 위치에 따라 운구 비용이 달라질 수 있습니다. <b>{seaBurial.companyname}</b>{" "}
          금액을 포함한 정확한 전체 장례 비용을 알아보세요.
        </Box>
        <Box>
          또한 승선 인원에 따라 진행 여부에 어려움을 겪는 경우도 있습니다. 이러한 점을 미리 고려하여
          전문가의 도움을 받아 적절한 업체를 선정하거나 적절한 계획을 세우는 것이 좋습니다.
        </Box>
        <NextLink
          href="/match/request/"
          event={{
            newGtm: { path: "sea_burial_detail", trigger_and_target: "CTA-uv_match_request" },
          }}
        >
          <LargeButton
            buttonType={LARGEL_BUTTON_TYPE.ORANGE}
            css={{ fontSize: 16, fontWeight: 600 }}
          >
            <Box marginRight="12px">전체 장례 비용 알아보기</Box>
            <ColorIcon name="arrow_right_small" color={colors.white} width="20px" height="20px" />
          </LargeButton>
        </NextLink>
      </VStack>
      <VStack padding="80px 24px" alignItems="center" gap="20px">
        <Box textAlign="center" css={{ fontSize: 14, fontWeight: 600 }}>
          정확한 정보를 불러올 수 없습니다.
          <br />
          전문가 상담을 통해 도와드리겠습니다.
        </Box>

        <LargeButton
          onClick={() => {
            customEvent({
              newGtm: { path: "sea_burial_detail", trigger_and_target: "call" },
            });
            call("1666-9784");
          }}
          buttonType={LARGEL_BUTTON_TYPE.ORANGE}
          css={{ fontSize: 16, fontWeight: 600 }}
        >
          <Box marginRight="12px">장례 전문가와 상담하기</Box>
          <ColorIcon name="arrow_right_small" color={colors.white} width="20px" height="20px" />
        </LargeButton>
      </VStack>
    </VStack>
  );
}

function SeaBurialFee({ seaBurial }: { seaBurial: any }) {
  return (
    <Box margin="32px 20px 40px">
      <table
        css={css`
          width: 100%;
        `}
      >
        <thead
          css={css`
            background: ${colors.warmGray150};
            border-bottom: 1px solid ${colors.gray300};
            font-weight: bold;
            font-size: 12px;
            line-height: 18px;
          `}
        >
          <tr>
            <th
              css={css`
                padding: 6px;
                width: 30%;
              `}
            >
              사용료 항목
            </th>

            <th
              css={css`
                padding: 6px;
                width: 30%;
              `}
            >
              사용료 내역
            </th>

            <th
              css={css`
                padding: 6px;
                width: 40%;
              `}
            >
              요금
            </th>
          </tr>
        </thead>
        <tbody className="caption1 gray900">
          {seaBurial.packages && seaBurial.packages.length > 0 ? (
            seaBurial.packages.map((rent: any) => {
              return (
                <tr
                  key={rent.item}
                  css={css`
                    text-align: center;
                  `}
                >
                  <td
                    css={css`
                      padding: 10px;
                    `}
                  >
                    {rent.name}
                  </td>
                  <td
                    css={css`
                      padding: 10px;
                    `}
                  >
                    {rent.description}
                  </td>
                  <td
                    css={css`
                      padding: 10px;
                    `}
                  >
                    {rent.price && rent.price.toLocaleString() + " 원"}
                  </td>
                </tr>
              );
            })
          ) : (
            <td
              colSpan={3}
              css={css`
                width: 100%;
                height: 180px;
                background: ${colors.warmGray50};
                vertical-align: center;
              `}
            >
              <Box
                width="100%"
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                className="caption1"
              >
                가격 정보를 준비중입니다.
              </Box>
            </td>
          )}
        </tbody>
      </table>
    </Box>
  );
}
